<template lang="pug">
  div(:class="[{ check: check || editIsVisible, editIsVisible: editIsVisible, error, ['hideError']: error && noErrorMessage }, 'label-position-' + labelPosition, $style.txt]")

    .label(
      v-if="!['animated', 'border'].includes(labelPosition) && label"
      :class="[classes]")
      label.text(:for="txtId") {{ label }}

    .text-input
      .relative
        template(v-if="multiline")
          textarea(
            ref="input"
            :id="txtId"
            :value="modelValue"
            :placeholder="placeholderText"
            :class="['placeholder-gray-600 border border-border-inputs rounded-md resize-none w-full']"
            @input="$emit('update:modelValue', $event.target.value, $event)"
            @blur="event => { onBlur(); emit('blur', event) }"
            @focus="onFocus"
            @change="$emit('change', $event.target.value)"
            :disabled="disabled || editIsVisible"
            :autocomplete="autocomplete"
            :style="styles.join(';')"
            :required="true"
          )
        template(v-else)
          input(
            ref="input"
            :id="txtId"
            :type="type"
            :value="modelValue"
            :placeholder="placeholderText"
            :class="['placeholder-gray-600 p-ds-md border border-border-inputs rounded-md']"
            @input="$emit('update:modelValue', $event.target.value, $event)"
            @blur="event => { onBlur(); emit('blur', event) }"
            @focus="onFocus"
            @change="$emit('change', $event.target.value)"
            :disabled="disabled || editIsVisible"
            :autocomplete="autocomplete"
            :style="styles.join(';')"
            :required="true"
            :min="min"
            :max="max")

        .bottom-right-icon(
          v-if="iconBottomRight"
          :class="$style.bottomRightIcon")
          .ai-icon(:class="$style.aiIcon")

        div(
          v-if="iconLeft"
          class="left-0 ml-ds-xxxs"
          :class="$style.placeholderIcon")
          Icon(
            :color="iconColor"
            :name="iconLeft"
            :size="iconFontSize"
            :style="iconStyles.join(';')")

        div(
          v-if="iconRight && !editIsVisible && !check"
          class="right-0 mr-ds-xxxs"
          :class="$style.placeholderIcon")
          Icon(
            :color="iconColor"
            :name="iconRight"
            :size="iconFontSize"
            :style="iconStyles.join(';')")

        .check(
          v-show="check || editIsVisible"
          :class="[{ 'checkWithEdit': editIsVisible }, $style.placeholderIcon]")
          Icon(
            name="i:check-solid"
            :size="14")

        .edit(
          v-if="editIsVisible"
          :class="$style.placeholderIcon")
          a(@click="editIsClicked = true") Edit

        .label(
          v-if="['animated', 'border'].includes(labelPosition) && label"
          :class="[classes, labelBg ? 'bg-' + labelBg : 'label-bg-default']")
          label(:class="{'text-base-dark transition-all': true}" :for="txtId") {{ label }}

      .msg.flex(v-if="error && !noErrorMessage")
        Icon(
          name="i:triangle-exclamation-solid"
          :size="16")
        .text {{ error }}

</template>

<script setup lang="ts">
import { getSizeClasses } from "~/scripts/css-helper"

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  /** Add a green check icon */
  check: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ""
  },
  labelPosition: {
    type: String,
    default: "top",
    validator: value => ["top", "left", "animated", "border"].includes(value)
  },
  labelBg: {
    type: String,
    default: ""
  },
  error: {
    type: String,
    default: ""
  },
  /** Hide error message - just red placeholder and border */
  noErrorMessage: {
    type: Boolean,
    default: false
  },
  /** Standard input types */
  type: {
    type: String,
    default: "text"
  },
  size: {
    type: String,
    default: "xl",
    validator: value => ["xs", "s", "m", "l", "xl"].includes(value)
  },
  inputId: {
    type: String
  },
  placeholder: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  checkAndDisable: {
    type: Boolean,
    default: false
  },
  focused: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: "off",
    required: false
  },
  /** Icon shortcut */
  iconLeft: {
    type: String,
    default: ""
  },
  /** Icon shortcut */
  iconRight: {
    type: String,
    default: ""
  },
  /** Icon size */
  // eslint-disable-next-line vue/require-default-prop
  iconSize: {
    type: Number,
    required: false
  },
  /** Icon colour */
  // eslint-disable-next-line vue/require-default-prop
  iconColor: {
    type: String,
    required: false
  },
  min: {
    type: Number,
    required: false,
    default: null
  },
  max: {
    type: Number,
    required: false,
    default: null
  },
  showArrows: {
    type: Boolean,
    default: false
  },
  border: {
    type: Boolean,
    default: true
  },
  backgroundColor: {
    type: String,
    default: ""
  },
  iconPadding: {
    type: Number,
    required: false,
    default: null
  },
  iconPaddingEnabled: {
    type: Boolean,
    default: false
  },
  iconBottomRight: {
    type: String,
    default: ""
  },
  customClasses: {
    type: String,
    default: ""
  },
  multiline: {
    type: Boolean,
    default: false
  }
})

const input = ref(null)
const activeFlag = ref(false)
const editIsClicked = ref(false)
const xPadding = ref(null)

const emit = defineEmits(["update:modelValue", "change", "focus", "blur", "input-loaded"])

defineExpose({
  input
})

const placeholderText = computed(() => {
  return props.labelPosition !== "animated" ? props.placeholder : ""
})
const classes = computed(() => getSizeClasses(props.size))
const editIsVisible = computed(() => props.checkAndDisable && !editIsClicked.value)
const iconFontSize = computed(() => {
  let size = 32

  if (props.iconSize) {
    size = props.iconSize
  } else {
    switch (size) {
      case "xs":
        size = 16
        break
      case "s":
        size = 18
        break
      case "m":
        size = 20
        break
      case "l":
        size = 24
        break
      case "xl":
        size = 32
        break
    }
  }

  return size
})
const iconStyles = computed(() => {
  const styles = []

  if (props.iconPaddingEnabled) {
    if (props.iconLeft || props.iconRight) {
      styles.push(`width: ${xPadding.value}px`)
    }
  }

  if (props.iconColor) {
    styles.push(`color: ${props.iconColor}`)
  }

  return styles
})

const styles = computed(() => {
  const styles = []

  if (props.iconPaddingEnabled) {
    if (props.iconLeft) {
      styles.push(`padding-left: ${xPadding.value}px`)
    }

    if (props.iconRight) {
      styles.push(`padding-right: ${xPadding.value}px`)
    }
  }

  if (!props.border) {
    styles.push(`border: 0`)
  }

  if (props.backgroundColor) {
    styles.push(`background-color: ${props.backgroundColor}`)
  }

  return styles
})

const txtId = computed(() =>
  props.inputId ? props.inputId : "txt-" + useId().replace(/[^a-zA-Z0-9]/g, "_")
)

onMounted(() => {
  if (props.focused) {
    focus()
  }

  if (props.iconLeft || props.iconRight) {
    if (props.iconPadding) {
      xPadding.value = props.iconPadding
    } else {
      xPadding.value = props.iconSize ? props.iconSize * 2 : iconFontSize.value * 2
    }
  }

  emit("input-loaded")
})

function focus() {
  input.value.focus()
}

function onFocus() {
  activeFlag.value = true

  if (props.autocomplete === "off" && input.value) {
    input.value.setAttribute("autocomplete", "chrome-off")
  }

  emit("focus")
}

function onBlur() {
  activeFlag.value = false

  if (props.autocomplete === "off" && input.value) {
    input.value.setAttribute("autocomplete", "off")
  }

  emit("blur")
}
</script>

<style lang="scss" module>
:global {
  :local(.txt) {
    position: relative;

    input {
      @apply text-gray-800 transition-all;
      background-color: transparent;
      opacity: 1; /* required on iOS */
      width: 100%;

      &:focus:not([readonly]) {
        @apply border-blue-primary;
      }

      &.xs-size {
        padding: 8px;
      }

      &[readonly],
      &[disabled] {
        cursor: not-allowed;
      }

      &:not(.showArrows) {
        &[type="number"]::-webkit-inner-spin-button,
        &[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
        }

        &[type="number"] {
          -moz-appearance: textfield;
        }
      }
    }

    textarea {
      @apply text-gray-800 transition-all;
      background-color: transparent;
      opacity: 1;
      width: 100%;
      padding: 12px;
      padding-right: 40px; // Make room for AI icon

      &:focus:not([readonly]) {
        @apply border-blue-primary;
      }

      &[readonly],
      &[disabled] {
        cursor: not-allowed;
      }
    }

    &.error {
      input,
      textarea {
        @apply border-red-primary;
        border-bottom-color: transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &.hideError {
      input,
      textarea {
        @apply border-red-primary rounded text-red-primary;

        &::placeholder {
          @apply text-red-primary;
        }
      }
    }

    &.label-position-left {
      @apply flex text-base;
    }

    input:focus ~ .msg,
    textarea:focus ~ .msg {
      box-shadow: 0 0 0 2px lighten(#c7c7c7, 10%);
    }

    .text-input {
      position: relative;
    }

    .label {
      display: table;
      padding-right: 1em;

      .text {
        vertical-align: middle;
        display: table-cell;
      }

      label {
        @apply text-base;
      }
    }

    .msg {
      @apply font-latoMedium border border-solid border-red-primary;
      padding: 7.5px 15px;
      border-bottom-left-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
      @apply text-red-primary;
      background-color: #f7e9eb;

      .icon {
        flex: none;
        margin-top: 2px;
      }

      .text {
        @apply text-base;
        display: inline-block;
        font-size: 16px;
        position: relative;
        top: -1px;
        font-weight: bold;
        margin-left: 5px;
        word-break: break-word;
      }
    }

    &.check {
      input {
        padding-right: 36px;
      }
      .check {
        right: 14px;
        @apply text-green-primary;
      }
    }

    &.editIsVisible {
      input {
        padding-right: 68px;
      }

      .check.checkWithEdit {
        right: 50px;
      }

      .edit {
        right: 14px;
      }
    }

    &.label-position-animated {
      input,
      textarea {
        padding: 16px;
        font-size: 16px;
        box-sizing: border-box;
        background: #fff;
        border: 1px solid #c9c9c9;
        border-radius: 6px;
        line-height: 1.5;
        resize: none;
      }

      .label {
        position: absolute;
        top: 16px;
        left: 14px;
        font-size: 16px;
        pointer-events: none;
        transition: all 0.2s ease-in-out;
        z-index: 1;
        background-color: transparent;
        height: auto;
        padding: 0 6px;

        label {
          color: var(--text-feint, var(--mono-tr-400, rgba(0, 0, 0, 0.39))) !important;
          transition:
            color 0.2s ease-in-out,
            font-size 0.2s ease-in-out; /* Add font-size transition */
          font-size: 16px;
        }
      }

      input:focus ~ .label,
      input:valid ~ .label,
      textarea:not(:placeholder-shown) ~ .label {
        top: -14px;
        font-size: 14px;
        background-color: white;
        z-index: 1;

        label {
          color: var(--text-body, var(--mono-tr-800, rgba(0, 0, 0, 0.75))) !important;
          transition:
            color 0.2s ease-in-out,
            font-size 0.2s ease-in-out; /* Include font-size transition */
          font-size: 14px; /* Add this line to explicitly set the font size */
        }
      }

      input::placeholder,
      textarea::placeholder {
        color: var(--text-feint, var(--mono-tr-400, rgba(0, 0, 0, 0.39))) !important;
        opacity: 1;
      }

      input:-webkit-autofill ~ .label {
        top: 6px;
        font-size: 16px;
        background-color: transparent;

        label {
          color: var(--text-feint, var(--mono-tr-400, rgba(0, 0, 0, 0.39))) !important;
          font-size: 16px; /* Ensure font size is maintained for autofill */
        }
      }
    }
    &.label-position-border {
      input:valid ~ .label,
      textarea {
        padding: 12px;
        font-size: 16px;
        padding-right: 40px;
        box-sizing: border-box;
        background: #fff;
        border: 1px solid #c9c9c9;
        border-radius: 6px;
        min-height: 5rem;
        line-height: 1.5;
        resize: none;
      }

      .label {
        position: absolute;
        top: -10px;
        left: 12px;
        font-size: 14px;
        background: white;
        z-index: 1;
        height: auto;
        line-height: 1;
        padding: 0 6px;
      }

      .text-input {
        position: relative;
        height: 80px;
      }
      label {
        cursor: text;
        font-size: 14px;
      }

      .label-bg-default {
        background: rgb(255, 255, 255);
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0) 40%,
          rgba(255, 255, 255, 1) 45%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      // Need to do autofill separately because Firefox can't handle it
      input:-webkit-autofill ~ .label {
        @apply text-base -top-ds-md;
        left: 26px;
        padding: 0 4px;
        background-color: #fff;
        height: 20px;
      }

      &.hideError {
        .label {
          @apply text-red-primary;
        }
      }
    }
  }
}

.placeholderIcon {
  @apply absolute h-full top-0 flex items-center justify-center;
  width: 50px;
}

.bottomRightIcon {
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  position: absolute;
  z-index: 1;
  right: 4px;
  top: 45px;
  border-radius: 6px;
  pointer-events: none;
}

.aiIcon {
  width: 32px;
  height: 20px;
  background-image: url("@anyvan-ui/icons/svg/ai-icon.svg");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  pointer-events: none;
}

@screen md {
  .label:global(.label-position-border) {
    background: #fff;
  }
}
</style>
